import { DateTimeQuestion } from '../../../../types/questions';

export interface DatePickerBounds {
  minimumDate?: Date;
  maximumDate?: Date;
  /**
   * The two fields below describe a window the answer must fall *outside* of,
   * which is the inverse of minimumDate/maximumDate. They are named apart on
   * purpose: `notBetween` is the only condition that produces them, and
   * feeding them to the picker's minDate/maxDate would invert the validation
   * silently. Distinct names make that mistake a type error.
   */
  excludedFrom?: Date;
  excludedTo?: Date;
}

export interface DaysAndHours {
  days: number;
  hours: number;
}

export const convertHoursToDaysAndHours = (hours: number | string): DaysAndHours => {
  const totalHours = typeof hours === 'string' ? parseFloat(hours) : hours;
  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;

  return {
    days,
    hours: remainingHours,
  };
};

const truncateSeconds = (date: Date): Date => {
  date.setSeconds(0, 0);
  return date;
};

/**
 * Shared by `between` and `notBetween`, which apply the same two-sided offset to
 * `formAnswerCreatedAt` and differ only in which DatePickerBounds fields they populate.
 */
const computeOffsetWindow = (
  formAnswerCreatedAtDate: Date,
  offsetHoursToLeft: string | undefined,
  offsetHoursToRight: string | undefined,
  dateMode: 'date' | 'datetime' | 'time' | undefined,
): { from: Date; to: Date } | undefined => {
  if (!offsetHoursToLeft || !offsetHoursToRight) return undefined;

  const offsetHoursToLeftFloat = parseFloat(offsetHoursToLeft);
  const offsetHoursToRightFloat = parseFloat(offsetHoursToRight);
  if (isNaN(offsetHoursToLeftFloat) || isNaN(offsetHoursToRightFloat)) return undefined;

  const from = new Date(formAnswerCreatedAtDate);
  from.setHours(from.getHours() - offsetHoursToLeftFloat);
  if (dateMode === 'date') {
    from.setHours(0, 0, 0, 0);
  } else if (dateMode === 'time') {
    const startOfDay = new Date(formAnswerCreatedAtDate);
    startOfDay.setHours(0, 0, 0, 0);
    if (from < startOfDay) {
      from.setTime(startOfDay.getTime());
    }
    truncateSeconds(from);
  } else {
    truncateSeconds(from);
  }

  const to = new Date(formAnswerCreatedAtDate);
  to.setHours(to.getHours() + offsetHoursToRightFloat);
  if (dateMode === 'date') {
    to.setHours(23, 59, 59, 999);
  } else if (dateMode === 'time') {
    const endOfDay = new Date(formAnswerCreatedAtDate);
    endOfDay.setHours(23, 59, 59, 999);
    if (to > endOfDay) {
      to.setTime(endOfDay.getTime());
    }
    truncateSeconds(to);
  } else {
    truncateSeconds(to);
  }

  return { from, to };
};

export const getDatePickerBounds = (
  question: DateTimeQuestion,
  formAnswerCreatedAt?: Date,
): DatePickerBounds => {
  const {
    dateValidationType,
    rangeCondition,
    offsetHours,
    offsetHoursToLeft,
    offsetHoursToRight,
    dateMode,
  } = question;

  if (dateValidationType !== 'fixedRange' || !rangeCondition || !formAnswerCreatedAt) {
    return {};
  }

  const formAnswerCreatedAtDate = new Date(formAnswerCreatedAt);

  if (dateMode === 'date') {
    formAnswerCreatedAtDate.setHours(0, 0, 0, 0);
  } else if (dateMode === 'datetime') {
    truncateSeconds(formAnswerCreatedAtDate);
  }

  const datePickerBounds: DatePickerBounds = {};

  switch (rangeCondition) {
    case 'onlyToday':
      if (dateMode === 'time') {
        const truncatedFormAnswerCreatedAtDate = truncateSeconds(formAnswerCreatedAtDate);
        datePickerBounds.minimumDate = truncatedFormAnswerCreatedAtDate;
        datePickerBounds.maximumDate = truncatedFormAnswerCreatedAtDate;
      } else {
        const startOfDay = new Date(formAnswerCreatedAtDate);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(formAnswerCreatedAtDate);
        endOfDay.setHours(23, 59, 59, 999);
        datePickerBounds.minimumDate = startOfDay;
        datePickerBounds.maximumDate = endOfDay;
      }
      break;

    case 'noFutureDates':
      if (dateMode === 'date') {
        const endOfDay = new Date(formAnswerCreatedAtDate);
        endOfDay.setHours(23, 59, 59, 999);
        datePickerBounds.maximumDate = endOfDay;
      } else if (dateMode === 'time') {
        const startOfDay = new Date(formAnswerCreatedAtDate);
        startOfDay.setHours(0, 0, 0, 0);
        datePickerBounds.minimumDate = startOfDay;
        datePickerBounds.maximumDate = truncateSeconds(new Date(formAnswerCreatedAtDate));
      } else {
        datePickerBounds.maximumDate = truncateSeconds(new Date(formAnswerCreatedAtDate));
      }
      break;

    case 'noPastDates':
      if (dateMode === 'date') {
        const startOfDay = new Date(formAnswerCreatedAtDate);
        startOfDay.setHours(0, 0, 0, 0);
        datePickerBounds.minimumDate = startOfDay;
      } else if (dateMode === 'time') {
        datePickerBounds.minimumDate = truncateSeconds(formAnswerCreatedAtDate);
        const endOfDay = new Date(formAnswerCreatedAtDate);
        endOfDay.setHours(23, 59, 59, 999);
        datePickerBounds.maximumDate = endOfDay;
      } else {
        datePickerBounds.minimumDate = formAnswerCreatedAtDate;
      }
      break;

    case 'after':
      if (offsetHours) {
        const offsetHoursFloat = parseFloat(offsetHours);
        if (!isNaN(offsetHoursFloat) && offsetHoursFloat > 0) {
          if (dateMode === 'date') {
            const days = Math.ceil(offsetHoursFloat / 24);
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setDate(minDate.getDate() + 1);
            minDate.setHours(0, 0, 0, 0);
            datePickerBounds.minimumDate = minDate;

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setDate(maxDate.getDate() + days);
            maxDate.setHours(23, 59, 59, 999);
            datePickerBounds.maximumDate = maxDate;
          } else if (dateMode === 'time') {
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setMinutes(minDate.getMinutes() + 1);
            datePickerBounds.minimumDate = truncateSeconds(minDate);

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setHours(maxDate.getHours() + offsetHoursFloat);

            const endOfDay = new Date(formAnswerCreatedAtDate);
            endOfDay.setHours(23, 59, 59, 999);
            if (maxDate > endOfDay) {
              maxDate.setTime(endOfDay.getTime());
            }
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          } else {
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setMinutes(minDate.getMinutes() + 1);
            datePickerBounds.minimumDate = truncateSeconds(minDate);

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setTime(maxDate.getTime() + offsetHoursFloat * 60 * 60 * 1000);
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          }
        }
      }
      break;

    case 'afterOrEqual':
      if (offsetHours) {
        const offsetHoursFloat = parseFloat(offsetHours);
        if (!isNaN(offsetHoursFloat) && offsetHoursFloat >= 0) {
          const minDate = new Date(formAnswerCreatedAtDate);
          if (dateMode === 'date') {
            minDate.setHours(0, 0, 0, 0);
          } else {
            truncateSeconds(minDate);
          }
          datePickerBounds.minimumDate = minDate;

          const maxDate = new Date(formAnswerCreatedAtDate);
          maxDate.setHours(maxDate.getHours() + offsetHoursFloat);

          if (dateMode === 'date') {
            maxDate.setHours(23, 59, 59, 999);
          } else if (dateMode === 'time') {
            const endOfDay = new Date(formAnswerCreatedAtDate);
            endOfDay.setHours(23, 59, 59, 999);
            if (maxDate > endOfDay) {
              maxDate.setTime(endOfDay.getTime());
            }
            truncateSeconds(maxDate);
          } else {
            truncateSeconds(maxDate);
          }
          datePickerBounds.maximumDate = maxDate;
        }
      }
      break;

    case 'before':
      if (offsetHours) {
        const offsetHoursFloat = parseFloat(offsetHours);
        if (!isNaN(offsetHoursFloat) && offsetHoursFloat > 0) {
          if (dateMode === 'date') {
            const days = Math.ceil(offsetHoursFloat / 24);
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setDate(minDate.getDate() - days);
            minDate.setHours(0, 0, 0, 0);
            datePickerBounds.minimumDate = minDate;

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setDate(maxDate.getDate() - 1);
            maxDate.setHours(23, 59, 59, 999);
            datePickerBounds.maximumDate = maxDate;
          } else if (dateMode === 'time') {
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setHours(minDate.getHours() - offsetHoursFloat);

            const startOfDay = new Date(formAnswerCreatedAtDate);
            startOfDay.setHours(0, 0, 0, 0);
            if (minDate < startOfDay) {
              minDate.setTime(startOfDay.getTime());
            }
            datePickerBounds.minimumDate = truncateSeconds(minDate);

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setMinutes(maxDate.getMinutes() - 1);
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          } else {
            const minDate = new Date(formAnswerCreatedAtDate);
            minDate.setTime(minDate.getTime() - offsetHoursFloat * 60 * 60 * 1000);
            datePickerBounds.minimumDate = truncateSeconds(minDate);

            const maxDate = new Date(formAnswerCreatedAtDate);
            maxDate.setMinutes(maxDate.getMinutes() - 1);
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          }
        }
      }
      break;

    case 'beforeOrEqual':
      if (offsetHours) {
        const offsetHoursFloat = parseFloat(offsetHours);
        if (!isNaN(offsetHoursFloat) && offsetHoursFloat >= 0) {
          const minDate = new Date(formAnswerCreatedAtDate);
          minDate.setHours(minDate.getHours() - offsetHoursFloat);

          if (dateMode === 'date') {
            minDate.setHours(0, 0, 0, 0);
            datePickerBounds.minimumDate = minDate;
          } else if (dateMode === 'time') {
            const startOfDay = new Date(formAnswerCreatedAtDate);
            startOfDay.setHours(0, 0, 0, 0);
            if (minDate < startOfDay) {
              minDate.setTime(startOfDay.getTime());
            }
            datePickerBounds.minimumDate = truncateSeconds(minDate);
          } else {
            datePickerBounds.minimumDate = truncateSeconds(minDate);
          }

          const maxDate = new Date(formAnswerCreatedAtDate);
          if (dateMode === 'date') {
            maxDate.setHours(23, 59, 59, 999);
            datePickerBounds.maximumDate = maxDate;
          } else {
            datePickerBounds.maximumDate = truncateSeconds(maxDate);
          }
        }
      }
      break;

    case 'between': {
      const window = computeOffsetWindow(
        formAnswerCreatedAtDate,
        offsetHoursToLeft,
        offsetHoursToRight,
        dateMode,
      );
      if (window) {
        datePickerBounds.minimumDate = window.from;
        datePickerBounds.maximumDate = window.to;
      }
      break;
    }

    case 'notBetween': {
      const window = computeOffsetWindow(
        formAnswerCreatedAtDate,
        offsetHoursToLeft,
        offsetHoursToRight,
        dateMode,
      );
      if (window) {
        datePickerBounds.excludedFrom = window.from;
        datePickerBounds.excludedTo = window.to;
      }
      break;
    }

    default:
      break;
  }

  return datePickerBounds;
};

/**
 * Minutes-and-seconds-of-day for a Date, ignoring the calendar date.
 *
 * `time`-mode values are parsed fresh against `new Date()` on every render
 * (see `parseDateString`), so their date part always reads as "today" and
 * can drift from `formAnswerCreatedAt`'s day (e.g. a form left open across
 * midnight), even though the two describe the same clock time. Comparing
 * full `Date` objects then silently accepts or rejects the wrong side.
 * `computeOffsetWindow` already clamps a `time`-mode window inside a single
 * day, so comparing only the time-of-day here mirrors what MUI itself does
 * for `minTime`/`maxTime` on the same pickers.
 */
const timeOfDayMs = (date: Date): number =>
  ((date.getHours() * 60 + date.getMinutes()) * 60 + date.getSeconds()) * 1000 +
  date.getMilliseconds();

const isWithinExcludedWindow = (
  candidate: Date,
  bounds: DatePickerBounds,
  dateMode?: 'date' | 'datetime' | 'time',
): boolean => {
  if (!bounds.excludedFrom || !bounds.excludedTo) return false;

  if (dateMode === 'time') {
    const candidateMs = timeOfDayMs(candidate);
    return candidateMs >= timeOfDayMs(bounds.excludedFrom) && candidateMs <= timeOfDayMs(bounds.excludedTo);
  }

  return candidate >= bounds.excludedFrom && candidate <= bounds.excludedTo;
};

/**
 * Whether a candidate value satisfies the question's date validation.
 *
 * Exported so the same rule can be asked from more than one place: the
 * component's inline check, the picker predicates, and later the submit-time
 * path (DS-4177). Keeping it here rather than inside the component is what
 * makes that possible.
 *
 * Mirrors the comparison the component has always made: `between` and the
 * other bounded conditions pass on equality with either endpoint, so
 * `notBetween` rejects on equality (D9).
 *
 * The excluded window and the minimum/maximum pair are independent
 * constraints, evaluated as a conjunction rather than as alternatives: a
 * caller combining both (a relative range with two reference questions,
 * DS-4176/DS-4177) must have both enforced, not just one.
 */
export const isDateWithinValidation = (
  candidate: Date,
  bounds: DatePickerBounds,
  dateMode?: 'date' | 'datetime' | 'time',
): boolean => {
  if (isWithinExcludedWindow(candidate, bounds, dateMode)) {
    return false;
  }

  if (bounds.minimumDate && candidate < bounds.minimumDate) {
    return false;
  }

  return !(bounds.maximumDate && candidate > bounds.maximumDate);
};

/**
 * Whether a whole calendar day sits inside the excluded window, for the picker's day grid.
 *
 * Only fully covered days are disabled. A boundary day still holds acceptable hours, and
 * greying it out would put them out of reach; on `datetime` the clock predicate below handles
 * that day's hours instead.
 */
export const isDayFullyExcluded = (day: Date, bounds: DatePickerBounds): boolean => {
  if (!bounds.excludedFrom || !bounds.excludedTo) return false;
  const startOfDay = new Date(day);
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date(day);
  endOfDay.setHours(23, 59, 59, 999);
  return startOfDay >= bounds.excludedFrom && endOfDay <= bounds.excludedTo;
};

/**
 * Whether a clock unit is unavailable, for the picker's time views.
 *
 * Exact for every view, `hours` included. An earlier version rounded the hours view outward
 * (disabling the whole hour when any minute of it was excluded) to keep the clock from offering
 * a value the inline check would then reject. That predicate is also the one MUI feeds into its
 * own field-level validation (`validateTime`/`validateDateTime` call `shouldDisableTime` on the
 * full selected value, not just on rendered cells), so the outward rounding made MUI reject
 * already-legal values typed or picked mid-hour. Kept exact, the same predicate is safe to reuse
 * for both purposes; the cost is that a partly-excluded hour cell no longer greys out as a whole
 * in the hours view, only the exact excluded minutes do once selected.
 *
 * `dateMode` is threaded through so a `time`-mode window compares only the clock's hours,
 * minutes and seconds, not the calendar date (see `isWithinExcludedWindow`).
 */
export const isTimeUnitExcluded = (
  value: Date,
  view: 'hours' | 'minutes' | 'seconds',
  bounds: DatePickerBounds,
  dateMode?: 'date' | 'datetime' | 'time',
): boolean => isWithinExcludedWindow(value, bounds, dateMode);
